<template>
  <v-stepper v-model="e1">
    <v-stepper-header>
      <v-stepper-item
        :complete="e1 > 1"
        :value="1"
        title="Settlement Options"
      />

      <v-divider />

      <v-stepper-item
        :complete="e1 > 2"
        :value="2"
        title="Select Term and Amount"
      />

      <v-divider />

      <v-stepper-item
        :complete="e1 > 3"
        :value="3"
        title="Payment Method"
      />
    </v-stepper-header>

    <v-stepper-window>
      <!--Settlement Options-->
      <v-stepper-window-item
        class="settlement-amount-step"
        :value="1"
      >
        <v-container>
          <v-row>
            <v-col cols="auto">
              <v-alert
                color="info"
                theme="dark"
                icon="mdi-bullhorn-outline"
              >
                <div class="text-h6">
                  Agent, Please read the following to customer:
                </div>
                Our reduced payoff plan allows you (the borrower) to choose how much
                you can afford to pay and over what term. You are eligible to select from the Max,
                Mid and Min options that we offer.
              </v-alert>
            </v-col>
          </v-row>
          <v-row
            justify="center"
          >
            <v-container fluid>
              <v-radio-group
                v-model="selectedPayoffOption"
              >
                <v-radio
                  v-for="(planOption, index) in payoffPlanOptions"
                  :key="`planOption-${index}`"
                  :value="planOption"
                  :disabled="!planOption.isEligible"
                >
                  <template #label>
                    <v-row>
                      <v-col cols="6">
                        <p>Option {{ planOption.option }}: {{ planOption.title }}</p>
                        <p>({{ planOption.terms }} Months)</p>
                      </v-col>
                      <v-col
                        cols="3"
                        align-self="center"
                      >
                        <p>{{ filters.currency(planOption.discountedSettlementAmount) }}</p>
                      </v-col>
                      <v-col
                        cols="3"
                        align-self="center"
                      >
                        <v-chip :disabled="!planOption.isEligible">
                          {{ planOption.isEligible ? 'Eligible' : 'Not Eligible' }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-container>
          </v-row>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="goToSinglePaymentView"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Single Payment Option
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="!selectedPayoffOption"
                @click="e1 = 2"
              >
                Continue to Select Terms
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-window-item>
      <!--Settlement Options End-->

      <!--Terms and amount screen-->
      <v-stepper-window-item :value="2">
        <v-container>
          <v-row justify="center">
            <v-col cols="auto">
              <h2> PAYOFF: {{ filters.currency(selectedPayoffOption?.discountedSettlementAmount) }}</h2>
            </v-col>
          </v-row>
          <v-row
            justify="center"
          >
            <v-container fluid>
              <v-radio-group
                v-model="selectedPayoffTerm"
              >
                <v-radio
                  v-for="(planTerm, index) in planTerms"
                  :key="`planTerm-${index}`"
                  :value="planTerm"
                >
                  <template #label>
                    <v-row>
                      <v-col
                        cols="auto"
                        align-self="center"
                      >
                        <p>
                          Pay over {{ planTerm.term }} Months,
                          {{ filters.currency(planTerm.paymentAmount) }} {{ paymentFrequencyStr }}
                        </p>
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-container>
          </v-row>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="e1 = 1"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Back
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="!selectedPayoffTerm || loading"
                @click="e1 = 3"
              >
                Continue to Payment Method
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-window-item>
      <!--Terms and amount screen End-->

      <!--Payment Method screen-->
      <v-stepper-window-item :value="3">
        <v-form
          ref="singlePaymentForm"
          novalidate
        >
          <v-container>
            <v-row>
              <v-col
                cols="12"
                align="right"
              >
                <ticket-add-debit-card-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
                <ticket-add-checking-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formData.paymentAccountId"
                  :items="paymentAccountsWithManualOption"
                  item-value="id"
                  item-title="title"
                  label="Payment Account"
                  outlined
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.firstPaymentDate"
                  type="date"
                  label="First Payment Date In Plan"
                  disabled
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="displayCSRMessageForManualPayment"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please note that because you selected the manual payments option,
                  you will be responsible for completing your payment.
                </v-alert>
              </v-col>

              <v-col
                v-else-if="displayCSRMessageForAutopay"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please acknowledge that you have verbally consented to the ACH Authorization.
                  The Electronic disclosure will be included in your confirmation email.
                </v-alert>
                <v-checkbox
                  v-model="formData.achConsent"
                  label="Verbally Opt into Autopay"
                />
                <v-card v-if="step === 1">
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <h2 class="talk__off-one_title">
                          <b>Representative:</b>
                        </h2>
                        <blockquote class="blockquote">
                          "I can help set up your recurring loan payment. By authorizing this,
                          you are allowing TBO Bank to electronically withdraw funds from your
                          account as agreed upon."
                        </blockquote>
                        <div class="talk__off-payment-details">
                          <h3>Please confirm the following details:</h3>

                          <ul>
                            <li>
                              <span>Payment Amount: ${{ recurringAutopayAmount }}</span>
                            </li>
                            <li>
                              <span>Payment Frequency: {{ paymentsFrequency }}</span>
                            </li>
                            <li>
                              <span>
                                First Payment Date:
                                {{ convertDateFormat(firstPaymentDate) }}
                              </span>
                            </li>
                            <li>
                              <span v-if="accountType === 'Debit Card'">
                                Debit Card Ending In: {{ accountLast4 }}
                              </span>
                              <span v-else>
                                Bank Account Ending In: {{ accountLast4 }}
                              </span>
                            </li>
                            <li>
                              <span>
                                Final Payment Date:
                                {{ convertDateFormat(finalPaymentDate) }}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-checkbox
                          v-model="electronicDisclosureConsent"
                          :label="`Do you confirm that these details are correct?`"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />

                    <v-btn
                      :disabled="electronicDisclosureConsent === false"
                      color="primary"
                      :loading="loading"
                      @click=" nextStep()"
                    >
                      <span>
                        Continue
                      </span>
                    </v-btn>
                  </v-card-actions>
                </v-card>
                <v-card v-if="step === 2">
                  <v-card-text>
                    <v-row>
                      <v-col>
                        <h2 class="talk__off-one_title">
                          <b>Representative:</b>
                        </h2>

                        <div class="talk__off-payment-details mt-2">
                          <h3>Under Federal Regulations, we must inform you that:</h3>

                          <ul>
                            <li>
                              <blockquote class="blockquote">
                                "You can cancel this authorization at any time by notifying us at
                                least three business days before your next payment by calling us
                                at 561-462-8527 or emailing us at supportteam@tbobank.com."
                              </blockquote>
                            </li>
                            <li>
                              <blockquote class="blockquote">
                                "A written confirmation of this authorization will be sent to
                                you at {{ customerEmail }}. Please review and keep it for
                                your records."
                              </blockquote>
                            </li>
                            <li>
                              <blockquote class="blockquote">
                                "If your payment amount is different, we will notify you at least
                                10 business days in advance."
                              </blockquote>
                            </li>
                          </ul>
                        </div>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col>
                        <v-checkbox
                          v-model="agreeFederalRegulations"
                          :label="`Do you confirm that these details are correct?`"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn
                      text
                      @click="goBack()"
                    >
                      Back
                    </v-btn>

                    <v-spacer />

                    <v-btn
                      :disabled="agreeFederalRegulations === false"
                      color="primary"
                      :loading="loading"
                      @click="nextStep()"
                    >
                      Continue
                    </v-btn>
                  </v-card-actions>
                </v-card>

                <v-card v-if="step === 3">
                  <v-card-text>
                    <v-row>
                      <v-col>
                        <h2 class="talk__off-one_title">
                          <b>Representative:</b>
                        </h2>

                        <div class="talk__off-payment-details mt-2">
                          <p>
                            Click on the create reduced payoff plan to create the
                            plan and send the EFT Document
                          </p>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn
                      text
                      @click="goBack()"
                    >
                      Back
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <v-col
                cols="12"
              >
                <v-checkbox
                  v-model="formData.isDebtConsolidation"
                  label="Processing Agents Only (Debt Consolidation Company)"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container>
            <v-row justify="space-between">
              <v-col
                cols="auto"
                align="left"
              >
                <v-btn
                  outlined
                  large
                  color="primary"
                  @click="e1 = 2"
                >
                  <v-icon
                    left
                    dark
                  >
                    mdi-arrow-left
                  </v-icon>
                  Back
                </v-btn>
              </v-col>
              <v-col
                cols="auto"
                align="right"
              >
                <v-btn
                  color="success"
                  large
                  :disabled="createSettlePlanButtonDisabled"
                  :loading="loading"
                  @click="createSettlementReducedPayOffPlan"
                >
                  Create Reduced Payoff Plan
                  <v-icon
                    right
                    dark
                  >
                    mdi-arrow-right
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-stepper-window-item>
      <!--Payment Method screen End-->
    </v-stepper-window>
  </v-stepper>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { DateTime } from 'luxon';
import { useFilters } from '@/composables/useFilters.js';
import TicketAddDebitCardDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddDebitCardDialog.vue';
import TicketAddCheckingDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddCheckingDialog.vue';

export default {
  name: 'ReducedPayoffPlan',

  components: {
    TicketAddCheckingDialog,
    TicketAddDebitCardDialog,
  },

  props: {
    ticket: {
      type: Object,
      required: true,
    },
    paymentAccounts: {
      type: Array,
      required: true,
    },
    payoffPlanOptions: {
      type: Array,
      required: true,
    },
    firstPaymentDateInPlan: {
      type: String,
      required: true,
    },
  },

  emits: [
    'display-single-payment-option-view',
    'payment-profiles-updated',
    'display-settlement-final-actions',
  ],

  setup() {
    const filters = useFilters();
    return { filters };
  },

  data() {
    return {
      e1: 1,
      selectedPayoffOption: null,
      selectedPayoffTerm: null,
      planTerms: [],
      formData: {
        settlementAmount: null,
        paymentAmount: null,
        settlementTerm: null,
        firstPaymentDate: this.firstPaymentDateInPlan,
        paymentAccountId: null,
        achConsent: false,
        platform: 'HUI',
        optionId: null,
        totalCreditAmount: null,
        discountPercent: 0,
        isDebtConsolidation: false,
      },
      loading: false,

      step: 1,
      electronicDisclosureConsent: false,
      agreeFederalRegulations: false,
    };
  },

  computed: {
    ...mapState('loanProLoan', ['amortizationSent', 'activeLoan']),
    ...mapState('settlement', ['pdsPaymentSchedule']),
    ...mapState('loanProCustomer', ['customer']),

    paymentAccountsWithManualOption() {
      const manualItem = {
        active: 1,
        id: -1,
        is_primary: 0,
        is_secondary: 0,
        title: 'Manual Payments',
        type: 'paymentAccount.type.manual',
        visible: 1,
      };
      return [
        ...this.paymentAccounts,
        manualItem,
      ];
    },

    recurringAutopayAmount() {
      return this.formData?.paymentAmount ?? 0;
    },

    paymentsFrequency() {
      return this.paymentFrequencyStr[0].toUpperCase() + this.paymentFrequencyStr.slice(1);
    },

    firstPaymentDate() {
      return this.formData?.firstPaymentDate ?? '';
    },

    finalPaymentDate() {
      const lastItem = this.pdsPaymentSchedule[this.pdsPaymentSchedule.length - 1];
      return lastItem?.date ?? '';
    },

    accountLast4() {
      return this.paymentProfileSelected?.title?.slice(-4) ?? '';
    },

    accountType() {
      return this.paymentProfileSelected?.type === 'paymentAccount.type.checking'
        ? 'Bank Account'
        : 'Debit Card';
    },

    isManualPayment() {
      return this.formData.paymentAccountId === -1;
    },
    displayCSRMessageForManualPayment() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => payment.type === 'paymentAccount.type.manual' && payment.id === this.formData.paymentAccountId);
    },
    displayCSRMessageForAutopay() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => ['paymentAccount.type.checking', 'paymentAccount.type.credit'].includes(payment.type) && payment.id === this.formData.paymentAccountId);
    },
    createSettlePlanButtonDisabled() {
      if (!this.formData.paymentAccountId) {
        return true;
      }

      if (!this.formData.firstPaymentDate) {
        return true;
      }

      if (!this.isManualPayment && !this.formData.achConsent) {
        return true;
      }

      if (
        !this.isManualPayment
        && (
          !this.electronicDisclosureConsent
          || !this.agreeFederalRegulations
        )
      ) {
        return true;
      }

      return this.loading;
    },
    paymentFrequencyStr() {
      return (this.ticket.active_loan.loan_setup.payment_frequency).split('.')[2];
    },
    paymentProfileSelected() {
      return this.paymentAccounts.find((payment) => payment.id === this.formData.paymentAccountId);
    },

    schedulesFormatted() {
      return this.scheduleValidDate(this.pdsPaymentSchedule).map((schedule) => ({
        title: schedule.name,
        date: this.convertDateFormat(schedule.date),
        charge_amount: schedule.payment_amount,
      }));
    },

    loanDisplayId() {
      return this.ticket?.active_loan?.display_id ?? '';
    },

    customerEmail() {
      return this.customer?.email ?? '';
    },

    customerName() {
      return `${this.customer?.first_name} ${this.customer?.last_name}` ?? '';
    },

  },

  watch: {
    'formData.paymentAccountId': {
      handler(newVal) {
        if (!newVal) {
          this.formData.achConsent = false;
        }
      },
    },

    electronicDisclosureConsent(newValue) {
      if (newValue && this.agreeFederalRegulations) {
        this.formData.achConsent = true;
      }
    },
    agreeFederalRegulations(newValue) {
      if (newValue && this.electronicDisclosureConsent) {
        this.formData.achConsent = true;
      }
    },
    selectedPayoffTerm(newVal) {
      if (newVal) {
        this.formData.settlementTerm = newVal.term;
        this.formData.paymentAmount = newVal.paymentAmount;
        this.updatePaymentSchedules();
      }
    },

    selectedPayoffOption(newVal) {
      if (newVal) {
        this.formData.settlementAmount = newVal.discountedSettlementAmount;
        this.formData.optionId = newVal.option;
        this.formData.totalCreditAmount = newVal.totalCreditAmount;
        this.formData.discountPercent = newVal.discountPercent;
        this.getPaymentAmountForTermsInPlan();
      }
    },
  },

  methods: {
    ...mapActions('settlement', [
      'createSettlementSinglePaymentPlan',
      'createSettlementReducedPayoffPlan',
      'getPeriodPaymentAmountForSettlementTerms',
      'getPdsSettlementSchedules',
      'updatePdsPaymentSchedule',
    ]),
    ...mapActions('notification', ['setNotification']),
    ...mapActions('loanProLoan', ['sendSettlementAmortization']),

    convertDateFormat(date) {
      return date ? DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('MM/dd/yyyy') : null;
    },

    async updatePaymentSchedules() {
      try {
        this.loading = true;

        const response = await this.getPdsSettlementSchedules({
          ticketId: this.ticket.crm.id,
          lpLoan: this.ticket.active_loan.id,
          formData: this.formData,
        });

        await this.updatePdsPaymentSchedule(response.data);
      } catch (e) {
        this.loading = false;
        const msg = 'Something went wrong while trying to get payment amount for each terms in the plan';
        await this.setNotification({
          message: msg,
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.step -= 1;
    },

    nextStep() {
      this.step += 1;
    },

    goToSinglePaymentView() {
      this.$emit('display-single-payment-option-view');
    },

    async reloadPaymentProfiles() {
      const res = await this.$axios
        .post(`api/v1/loanpro-customer/${this.ticket.lpCustomer.id}/payment-profiles`, {
          ticketId: this.$route.params.id,
        });

      this.$emit('payment-profiles-updated', res.data.data);
    },

    async getPaymentAmountForTermsInPlan() {
      try {
        const result = await this.getPeriodPaymentAmountForSettlementTerms({
          ticketId: this.ticket.crm.id,
          lpLoan: this.ticket.active_loan.id,
          data: {
            settlementAmount: this.selectedPayoffOption.discountedSettlementAmount,
            terms: this.selectedPayoffOption.terms,
          },
        });

        if (result.status === 200) {
          this.planTerms = result.data;
        }
      } catch (e) {
        this.loading = false;
        const msg = 'Something went wrong while trying to get payment amount for each terms in the plan';
        this.setNotification({
          message: msg,
          color: 'red',
        });
      }
    },

    async createSettlementReducedPayOffPlan() {
      this.loading = true;

      try {
        const result = await this.createSettlementReducedPayoffPlan({
          ticketId: this.ticket.crm.id,
          lpLoan: this.ticket.active_loan.id,
          formData: this.formData,
        });

        if (result.status === 200) {
          if (!this.isManualPayment) {
            await this.sendSettlementAmortization({
              loanId: this.activeLoan.id,
              ticketId: this.$route.params.id,
              amortizationSchedules: this.schedulesFormatted,
              name: this.customerName,
              bankAccountType: this.accountType,
              accountLast4: this.accountLast4,
              loanDisplayId: this.loanDisplayId,
              routingNumber: this.paymentProfileSelected?.checking_account?.routing_number ?? '',
            });
          }

          const msg = 'Reduced Payoff Settlement Plan was completed';
          await this.setNotification({
            message: msg,
            color: 'green',
          });
          this.$emit('display-settlement-final-actions');
        }
      } catch (e) {
        this.loading = false;
        const msg = 'There was an error processing your request';
        await this.setNotification({
          message: msg,
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },

    scheduleValidDate(schedules) {
      return schedules.filter((schedule) => {
        const scheduleDate = DateTime.fromSQL(schedule.date).startOf('day');
        const today = DateTime.now().startOf('day');

        return scheduleDate >= today;
      });
    },

  },
};
</script>

<style lang="scss" scoped>
  :deep(.v-selection-control) {
    border: 1px solid #ccc;
    margin: 0.5rem 0;
    padding: 0.5rem;
    background: #FFFFFF;
    width: 100%;
  }

  :deep(.v-selection-control .v-label) {
    width: 100%;
    opacity: 1;
  }

  :deep(.v-selection-control .v-label p) {
    font-weight: 600;
    font-size: 1.0rem;
  }
</style>
